/**
 * Servicio para consultar reportes de tiempo de uso y auditoría
 */
import { api } from '../config/api';

const auditService = {
  /**
   * Obtener slices de actividad del usuario actual
   */
  getActivitySlices: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    const url = `/audit/activity-slices/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    // DRF devuelve el array directamente cuando no hay paginación
    return Array.isArray(response) ? response : (response?.data || response || []);
  },

  /**
   * Reporte de tiempo de uso por usuario y día (REAL basado en sesiones)
   */
  getTimeUsageReport: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    // Usar el nuevo endpoint basado en UserUsageSession (tiempo real)
    // DRF actions: /audit/activity-slices/real_usage_report/ (con guión bajo en el nombre)
    const url = `/audit/activity-slices/real_usage_report/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('[AUDIT SERVICE] Llamando a real-usage-report:', url);
    console.log('[AUDIT SERVICE] Params:', params);
    
    try {
      const response = await api.get(url);
      console.log('[AUDIT SERVICE] Respuesta completa real-usage-report:', response);
      console.log('[AUDIT SERVICE] Tipo de respuesta:', typeof response);
      console.log('[AUDIT SERVICE] Es array:', Array.isArray(response));
      console.log('[AUDIT SERVICE] Tiene usage_by_user_day:', !!response?.usage_by_user_day);
      
      // DRF puede devolver el objeto directamente o dentro de .data
      let data = response;
      if (response?.data && !response.usage_by_user_day) {
        data = response.data;
        console.log('[AUDIT SERVICE] Datos en response.data');
      }
      
      // El nuevo endpoint devuelve { usage_by_user_day: [], totals_by_user: [] }
      if (data && data.usage_by_user_day && Array.isArray(data.usage_by_user_day)) {
        console.log('[AUDIT SERVICE] usage_by_user_day encontrado, cantidad:', data.usage_by_user_day.length);
        
        // Convertir el formato nuevo al formato esperado por el frontend
        const formattedData = data.usage_by_user_day.map(item => {
          // El campo date puede venir como string ISO o como objeto Date
          let dateValue = item.date;
          if (typeof dateValue === 'string') {
            // Si es string, mantenerlo como está (formato YYYY-MM-DD)
            dateValue = dateValue.split('T')[0]; // Extraer solo la fecha si tiene timestamp
          }
          
          return {
            user_id: item.user_id,
            user_name: item.user__nombre || item.user_name || 'Usuario',
            user_email: item.user__email || item.user_email || '',
            date: dateValue,
            total_seconds: item.total_seconds || 0,
            total_minutes: Math.round((item.total_seconds || 0) / 60 * 100) / 100,
            total_hours: Math.round((item.total_seconds || 0) / 3600 * 100) / 100,
            slice_count: item.session_count || 0, // Mantener nombre para compatibilidad
            session_count: item.session_count || 0,
            event_count: 0, // No aplica para sesiones reales
            morning_seconds: 0, // TODO: Calcular si es necesario
            afternoon_seconds: 0, // TODO: Calcular si es necesario
          };
        });
        
        console.log('[AUDIT SERVICE] Datos formateados:', formattedData);
        console.log('[AUDIT SERVICE] Primer registro:', formattedData[0]);
        return formattedData;
      }
      
      // Si no tiene usage_by_user_day, puede ser que esté vacío o en otro formato
      if (data && Array.isArray(data)) {
        console.log('[AUDIT SERVICE] Respuesta es array directo');
        return data;
      }
      
      // Si es un objeto pero no tiene usage_by_user_day, puede estar vacío
      if (data && typeof data === 'object') {
        console.log('[AUDIT SERVICE] Respuesta es objeto, keys:', Object.keys(data));
        if (data.usage_by_user_day === null || (Array.isArray(data.usage_by_user_day) && data.usage_by_user_day.length === 0)) {
          console.log('[AUDIT SERVICE] No hay datos de uso (array vacío o null)');
          return [];
        }
      }
      
      console.warn('[AUDIT SERVICE] Formato de respuesta no reconocido, retornando array vacío');
      return [];
    } catch (error) {
      console.error('[AUDIT SERVICE] Error en real-usage-report:', error);
      console.error('[AUDIT SERVICE] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: url
      });
      
        // Si falla, intentar con el endpoint antiguo como fallback
        try {
          console.log('[AUDIT SERVICE] Intentando fallback al endpoint antiguo...');
          const fallbackUrl = `/audit/activity-slices/time_usage_report/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const fallbackResponse = await api.get(fallbackUrl);
        console.log('[AUDIT SERVICE] Fallback exitoso');
        return Array.isArray(fallbackResponse) ? fallbackResponse : (fallbackResponse?.data || fallbackResponse || []);
      } catch (fallbackError) {
        console.error('[AUDIT SERVICE] Fallback también falló:', fallbackError);
        return [];
      }
    }
  },

  /**
   * Reporte de días sin actividad
   */
  getDaysWithoutActivity: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    const url = `/audit/activity-slices/days-without-activity/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    // DRF devuelve el objeto directamente
    return response?.data || response || {};
  },

  /**
   * Reporte de uso total del sistema
   * Calcula el total desde el reporte de sesiones reales
   */
  getTotalUsage: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    try {
      // Usar el endpoint de sesiones reales para obtener totales
      const url = `/audit/activity-slices/real_usage_report/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      console.log('[AUDIT SERVICE] getTotalUsage llamando a:', url);
      
      const response = await api.get(url);
      console.log('[AUDIT SERVICE] getTotalUsage respuesta:', response);
      
      // DRF puede devolver el objeto directamente o dentro de .data
      let data = response;
      if (response?.data && !response.totals_by_user) {
        data = response.data;
      }
      
      // Calcular totales desde totals_by_user
      if (data && data.totals_by_user && Array.isArray(data.totals_by_user)) {
        const totalSeconds = data.totals_by_user.reduce((sum, user) => sum + (user.total_seconds || 0), 0);
        const totalSessions = data.totals_by_user.reduce((sum, user) => sum + (user.session_count || 0), 0);
        const uniqueUsers = data.totals_by_user.length;
        
        return {
          total_seconds: totalSeconds,
          total_minutes: Math.round(totalSeconds / 60 * 100) / 100,
          total_hours: Math.round(totalSeconds / 3600 * 100) / 100,
          total_slices: totalSessions, // Mantener nombre para compatibilidad
          total_sessions: totalSessions,
          total_events: 0, // No aplica para sesiones reales
          unique_users: uniqueUsers,
          date_from: data.date_from,
          date_to: data.date_to,
        };
      }
      
      // Fallback al endpoint antiguo si no hay datos
      console.log('[AUDIT SERVICE] getTotalUsage: No hay datos en totals_by_user, usando fallback');
      const fallbackUrl = `/audit/activity-slices/total-usage/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const fallbackResponse = await api.get(fallbackUrl);
      return fallbackResponse?.data || fallbackResponse || {};
    } catch (error) {
      console.error('[AUDIT SERVICE] Error en getTotalUsage:', error);
      // Fallback al endpoint antiguo
      try {
        const fallbackUrl = `/audit/activity-slices/total-usage/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const fallbackResponse = await api.get(fallbackUrl);
        return fallbackResponse?.data || fallbackResponse || {};
      } catch (fallbackError) {
        console.error('[AUDIT SERVICE] Fallback también falló:', fallbackError);
        return {};
      }
    }
  },

  /**
   * Obtener logs de auditoría
   */
  getAuditLogs: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.action) queryParams.append('action', params.action);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    const url = `/audit/audit-logs/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('[AUDIT SERVICE] Llamando a:', url);
    console.log('[AUDIT SERVICE] Params:', params);
    
    try {
      const response = await api.get(url);
      console.log('[AUDIT SERVICE] Respuesta recibida:', response);
      console.log('[AUDIT SERVICE] Tipo de respuesta:', typeof response);
      console.log('[AUDIT SERVICE] Es array:', Array.isArray(response));
      
      // DRF devuelve el array directamente cuando no hay paginación
      // Si es array, retornarlo directamente
      if (Array.isArray(response)) {
        console.log('[AUDIT SERVICE] Retornando array directamente, cantidad:', response.length);
        return response;
      }
      
      // Si tiene .data, puede ser un objeto con paginación o estructura anidada
      if (response?.data) {
        console.log('[AUDIT SERVICE] Retornando response.data, cantidad:', Array.isArray(response.data) ? response.data.length : 'N/A');
        return Array.isArray(response.data) ? response.data : response.data;
      }
      
      // Si es un objeto pero no tiene .data, retornarlo tal cual
      console.log('[AUDIT SERVICE] Retornando response directamente');
      return response || [];
    } catch (error) {
      console.error('[AUDIT SERVICE] Error en getAuditLogs:', error);
      console.error('[AUDIT SERVICE] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Reporte de auditoría por usuario, acción y entidad
   */
  getAuditReport: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    
    const url = `/audit/audit-logs/audit-report/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);
    // DRF devuelve el array directamente cuando no hay paginación
    return Array.isArray(response) ? response : (response?.data || response || []);
  },
};

export default auditService;

